import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { EnvToken, SeoSegmentsToken } from '@app/core/env.tokens';
import { BehaviorSubject, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { TagService } from '@services/http/tag.service';
import { ReviewAggregationForTagResponse } from '@models/tag/reviewAggregationForTagResponse';
import { SeoReviewTypeInterface } from '@components/seoPageElements/price-and-review/interface/seo.review.type.interface';
import { Vertical } from '@models/vertical.model';
import { Task } from '@app/models/task.model';
import { JsonLdPipe } from '@app/pipes/json-ld-pipe';
import { TranslatePipe } from '@app/pipes/translate.pipe';
import { ReviewOverviewComponent } from '@components/reviewOverview/reviewOverview.component';
import { PriceAverageComponent } from '@app/pages/guides/priceAverage/priceAverage.component';
import { ReviewGridComponent } from '@components/seoPageElements/reviewGrid/reviewGrid.component';
import { takeUntil } from 'rxjs/operators';
import {
  LOCAL_MOCK_PRICES,
  LOCAL_MOCK_REVIEWS,
  LOCAL_MOCK_REVIEW_COUNT,
} from '@components/seoPageElements/price-and-review/price-and-review.local-mock';
import { LocalizationService } from '@services/logic/localization.service';

@Component({
  selector: 'app-price-and-review',
  templateUrl: './price-and-review.component.html',
  styleUrls: ['./price-and-review.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    JsonLdPipe,
    TranslatePipe,
    ReviewOverviewComponent,
    PriceAverageComponent,
    ReviewGridComponent,
  ],
})
export class PriceAndReviewComponent implements OnInit, OnDestroy {
  protected readonly env = inject(EnvToken);
  protected readonly seg = inject(SeoSegmentsToken);
  private readonly localization = inject(LocalizationService);
  private readonly cdr = inject(ChangeDetectorRef);

  private destroy$ = new Subject<void>();
  private _vertical = new BehaviorSubject<Vertical>({
    similiarTasks: [],
    taskExamples: [],
  });
  protected environment = this.env;

  @Input()
  set vertical(value) {
    this._vertical.next(value);
  }

  get vertical() {
    return this._vertical.getValue();
  }

  @Input() city?: { city: string };
  @Input() q25Price?: number = 0;
  @Input() q50Price?: number = 0;
  @Input() q75Price?: number = 0;
  @Input() cityOrRegionText?: string;
  @Input() relTasks?: Task[] = [];
  @Input() imgUrl?: string;

  @Output() componentRendered = new EventEmitter<boolean>();

  taskSchema: any;
  reviewCount!: ReviewAggregationForTagResponse;
  latestReviews: SeoReviewTypeInterface[] = [];
  didFetchReviews = false;
  private readonly isLocal = this.env.env === 'local';

  constructor(
    private tagService: TagService,
    private router: Router,
    private translate: TranslatePipe,
  ) {}

  ngOnInit() {
    if (this.vertical) {
      this._vertical.pipe(takeUntil(this.destroy$)).subscribe((value) => {
        if (value) {
          if (!this.didFetchReviews) {
            this.didFetchReviews = true;
            if (this.vertical?.dbTag) {
              this.fetchLatestReviews();
              this.fetchReviewCount();
            } else if (this.isLocal) {
              this.applyLocalMockData();
            }
          }
        }
      });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getPriceGuideURL(): string | null {
    if (!this.vertical?.priceGuide) {
      return null;
    }
    const category = this.vertical?.mainCategory?.slug;
    const subCategory = this.vertical?.subCategory?.slug;
    const guideSlug =
      this.vertical?.priceGuide?.newSlug || this.vertical?.priceGuide?.slug;

    const segments =
      category && subCategory
        ? [category, subCategory, guideSlug]
        : [category, guideSlug];
    // Guide pages canonicalize away duplicate consecutive segments
    // (`/handyman/handyman/pris` → `/handyman/pris`) — link there directly.
    const deduped = segments.filter(
      (segment, i) => segment !== segments[i + 1],
    );
    return `/${deduped.join('/')}/${this.seg.suffix.priceGuide}`;
  }

  fetchLatestReviews() {
    if (this.vertical.tagId) {
      // For mini-verticals, use relatedCategoryData.cat as keywords
      // For regular verticals, use cat or kW
      const keywords =
        (this.vertical as any)?.relatedCategoryData?.cat ||
        this.vertical?.cat ||
        this.vertical?.kW;

      // Only include region/city if they have values - undefined causes API errors
      const requestData: {
        keywords: string;
        tagId: number;
        region?: string;
        city?: string;
      } = {
        keywords: keywords,
        tagId: this.vertical?.tagId,
      };

      if (this.vertical?.region?.regionURL) {
        requestData.region = this.vertical.region.regionURL;
      }
      if (this.vertical?.city?.slug) {
        requestData.city = this.vertical.city.slug;
      }

      this.tagService
        .getLatestReviews(requestData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: SeoReviewTypeInterface[]) => {
            if (res && res.length > 0) {
              this.latestReviews = res;

              this.componentRendered.emit(true);
            } else if (this.isLocal) {
              this.applyLocalMockData();
            } else {
              this.latestReviews = [];

              this.componentRendered.emit(false);
            }
            this.cdr.markForCheck();
          },
          error: (err) => {
            if (this.isLocal) {
              this.applyLocalMockData();
              this.cdr.markForCheck();
              return;
            }
            this.latestReviews = [];
            this.componentRendered.emit(false);
            this.cdr.markForCheck();
          },
        });
    } else if (this.isLocal) {
      this.applyLocalMockData();
    } else {
      this.componentRendered.emit(false);
    }
  }

  /**
   * Local builds have no reviews or completed offers in the database, so the
   * section would never render during development. Never runs outside
   * `env.env === 'local'`.
   */
  private applyLocalMockData() {
    this.latestReviews = LOCAL_MOCK_REVIEWS;
    this.reviewCount = LOCAL_MOCK_REVIEW_COUNT;
    if (!this.q50Price) {
      this.q25Price = LOCAL_MOCK_PRICES.q25;
      this.q50Price = LOCAL_MOCK_PRICES.q50;
      this.q75Price = LOCAL_MOCK_PRICES.q75;
    }
    this.mapTaskSchema();
    this.componentRendered.emit(true);
  }

  fetchReviewCount() {
    if (this.vertical.tagId) {
      this.tagService
        .totalReviewsForTasksTag(this.vertical.tagId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: ReviewAggregationForTagResponse) => {
            if (res != null) {
              this.reviewCount = res;
              this.mapTaskSchema();
              this.cdr.markForCheck();
            }
          },
          // Without this, a failed call (SSR timeout, TLS error) surfaces as
          // an uncaughtException in PM2 logs instead of a missing count.
          error: () => {},
        });
    }
  }

  mapTaskSchema() {
    try {
      // Name the entity after the vertical, not its parent category — using
      // mainCategory.title gave every vertical under /have/ an identically
      // named Product ("Have og anlæg"). Mirrors setEnhancedServiceSchema.
      const title =
        this.vertical?.cat ||
        this.vertical?.sPM ||
        this.vertical?.mainCategory?.title;
      const { region, city } = this.vertical || {};
      this.taskSchema = {
        URL: `${this.env.domain}${this.router.url}`,
        name: `${title}${region ? ` ${region.name}` : ''}${
          city ? `${region ? ', ' : ' '}${this.city?.city}` : ''
        }`,
        '@type': 'Product',
        brand: {
          name: this.env.appName,
          '@type': 'Brand',
        },
        image: `${this.imgUrl}`,
        '@context': 'http://schema.org',
        description: `${this.translate.transform('META.FIND_LOCAL')} ${title}${
          this.cityOrRegionText
        }`,
      };

      // Only emit a rating once both values are known — the review count
      // arrives asynchronously, and undefined members serialise into the
      // JSON-LD as an incomplete AggregateRating.
      const reviewTotal = this.reviewCount?.count?.total;
      const reviewAverage = this.reviewCount?.average;
      if (reviewTotal && reviewAverage) {
        this.taskSchema.aggregateRating = {
          '@type': 'AggregateRating',
          itemReviewed: {
            '@type': 'Thing',
            name: title,
          },
          reviewCount: reviewTotal,
          ratingValue: Number(reviewAverage.toFixed(1)),
        };
      }

      // `relTasks` always arrives in major units: seo.component converts the
      // live-fetch path, and vertical.similiarTasks/taskExamples are stored
      // major already. Converting a second time here published every price
      // 100x too low (205 kr became 2.05).
      // Publish the same offers the page displays, by trimming to the q25–q75
      // band the widget renders. Two problems with the raw set:
      //
      // 1. Roughly 14% of bids sit at the platform minimum (205 kr on DK) and
      //    are frequently placed to win an assignment rather than to price the
      //    work, so a true `lowPrice` advertises a figure no handyhander will
      //    honour. Trimming to the band drops that cluster by construction,
      //    without a per-market floor constant this app doesn't carry.
      // 2. The widget shows the band, so an untrimmed range contradicts it —
      //    Google expects the markup to reflect the visible content.
      //
      // `lowPrice` stays honest because it is the minimum of the offers that
      // are actually published, not a bare percentile.
      //
      // No `user.rating` filter: the visible percentiles are computed over
      // every bid, and filtering here is what made the two disagree — pages
      // whose bidders were all unrated showed a price and published none.
      const bandLow = this.q25Price ?? 0;
      const bandHigh = this.q75Price ?? 0;
      const offers = (this.relTasks ?? [])
        .flatMap((relTask) => relTask?.offers || [])
        .filter((offerAndUser) => {
          const price = offerAndUser?.priceBid;
          return (
            typeof price === 'number' &&
            Number.isFinite(price) &&
            price >= bandLow &&
            price <= bandHigh
          );
        })
        .map((offerAndUser) => {
          const offer: Record<string, unknown> = {
            '@type': 'Offer',
            price: offerAndUser.priceBid,
            priceCurrency: this.localization.currencyCode().toUpperCase(),
          };
          if (offerAndUser.user?.firstName) {
            offer['name'] = offerAndUser.user.firstName;
          }
          return offer;
        });

      // An AggregateOffer carrying no offers is invalid markup — omit the node
      // rather than publishing lowPrice/highPrice/offerCount of 0. This also
      // covers the displayPrice fallback, where a band is shown but there are
      // no individual bids to stand behind it.
      if (bandHigh > 0 && offers.length) {
        const prices = offers.map((offer) => offer['price'] as number);
        this.taskSchema.offers = {
          '@type': 'AggregateOffer',
          offers,
          lowPrice: Math.min(...prices),
          highPrice: Math.max(...prices),
          offerCount: offers.length,
          priceCurrency: this.localization.currencyCode().toUpperCase(),
        };
      }
    } catch (err) {}
  }
}
